import { getSlug } from "@huub-web/utils";

import type { NavigationContext } from "../../contexts";
import type { NavigationState } from "../../types";
import { traverseNavigationState } from "./traverseNavigationState";
import { matchSegment, splitPath } from "./pathMatching";

/**
 * Parses a pathname using navigation keys as a template to extract dynamic parameter values.
 *
 * @param navigationState - The application state containing all navigation records
 * @param navigationKeys - The navigation template with placeholders (e.g., ["orientation", "[id]"])
 * @param pathname - The URL pathname to parse (e.g., "/orientation/123"). Optional parameter.
 * @returns
 *   - The navigation state with actual values (e.g., ["orientation", "123"]) if pathname matches
 *   - The navigationKeys as-is if pathname is undefined
 *   - null if pathname is null, empty string, or doesn't match the navigation structure
 *
 * @example
 * getParsedNavigationContextFromNavigationState(navigationState, ["orientation", "[id]"], "/orientation/123")
 * // Returns: ["orientation", "123"]
 *
 * getParsedNavigationContextFromNavigationState(navigationState, ["orientation", "[id]"])
 * // Returns: ["orientation", "[id]"]
 *
 * getParsedNavigationContextFromNavigationState(navigationState, ["orientation", "[id]"], "")
 * // Returns: null
 */
export const getParsedNavigationContextFromNavigationState = (
  navigationState: NavigationState,
  navigationKeys: NavigationContext,
  pathname?: string,
): NavigationContext | null => {
  if (!navigationState || !navigationKeys.length) {
    return null;
  }

  // If pathname is undefined (optional parameter not provided), return navigation keys as-is
  if (pathname === undefined) {
    return navigationKeys;
  }

  // If pathname is null or empty string, it's invalid - return null
  if (pathname === null || pathname === "") {
    return null;
  }

  const pathSegments = splitPath(pathname);

  // Special handling for root path "/"
  if (pathname === "/") {
    if (navigationKeys.length === 1) {
      // Check if the single navigation key matches a record with slug "/"
      const result = traverseNavigationState(
        navigationState,
        navigationKeys,
        ({ key, traversalResult }) => {
          const slug = getSlug(traversalResult.slug, ["[", "]"]);
          if (slug === "/" || slug === "") {
            return { continue: false, result: [key] };
          }
          return { continue: false, result: null };
        },
      );
      return result;
    }
    return null;
  }

  if (pathSegments.length !== navigationKeys.length) {
    // Path segments don't match the navigation keys structure
    return null;
  }

  const parsedNavigationState: string[] = [];
  let segmentIndex = 0;

  const result = traverseNavigationState(
    navigationState,
    navigationKeys,
    ({ key, remainingKeys, traversalResult }) => {
      const currentSegment = pathSegments[segmentIndex];

      if (!currentSegment) {
        return { continue: false, result: null };
      }

      const slug = getSlug(traversalResult.slug, ["[", "]"]);

      // Check if this navigation key is a placeholder
      if (key.startsWith("[") && key.endsWith("]")) {
        // Use matchSegment to handle dynamic extraction
        const matchResult = matchSegment(currentSegment, slug);

        if (matchResult.matches && matchResult.extractedValue !== undefined) {
          parsedNavigationState.push(matchResult.extractedValue);
        } else {
          return { continue: false, result: null };
        }
      } else {
        // Static key - verify it matches
        const matchResult = matchSegment(currentSegment, slug);

        if (!matchResult.matches) {
          return { continue: false, result: null };
        }

        // Use the static key as-is
        parsedNavigationState.push(key);
      }

      segmentIndex++;

      // If no more keys to process, we're done
      if (remainingKeys.length === 0) {
        return {
          continue: false,
          result:
            parsedNavigationState.length === navigationKeys.length
              ? parsedNavigationState
              : null,
        };
      }

      // Continue traversing
      return { continue: true };
    },
  );

  return result;
};
